import React, { useCallback, useRef, useState } from 'react'
import { Box, type BoxProps, LinearProgress, Link, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'

import { borderRadius, borders, colors, maxWidth, spacing, typography } from '~tokens/internal'

import Icon from './Icon'
import IconButton from './IconButton'

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

// ---------------------------------------------------------------------------
// FileUploadDropzone
// ---------------------------------------------------------------------------

export interface FileUploadDropzoneProps extends Omit<BoxProps, 'error' | 'onDrop'> {
  /** Whether the dropzone is in an error state */
  error?: boolean
  /** Error message shown below the title (replaces helperText when error is true) */
  errorMessage?: string
  /** Helper text shown below the title (e.g. accepted formats) */
  helperText?: string
  /** Custom link label for the clickable action */
  linkLabel?: string
  /** Callback with the dropped files */
  onDrop?: (files: FileList) => void
}

const dropzoneBaseSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: spacing.xxs,
  px: spacing.xs,
  py: spacing.sm,
  borderRadius: borderRadius.sm,
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
} as const

export const FileUploadDropzone: React.FC<FileUploadDropzoneProps> = ({
  error = false,
  errorMessage,
  helperText = 'SVG, PNG, JPG or GIF (max. 3MB)',
  linkLabel = 'Click to upload',
  onDrop,
  onClick,
  sx,
  ...boxProps
}) => {
  const theme = useTheme()
  const [dragActive, setDragActive] = useState(false)
  const dragCounter = useRef(0)

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current += 1
    setDragActive(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current -= 1
    if (dragCounter.current === 0) {
      setDragActive(false)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      dragCounter.current = 0
      setDragActive(false)
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        onDrop?.(e.dataTransfer.files)
      }
    },
    [onDrop],
  )

  const getBorderStyle = (): string => {
    if (error) return 'solid'
    return 'dashed'
  }

  const getBorderColor = (): string => {
    if (error) return theme.palette.error.main
    if (dragActive) return theme.palette.primary.main
    return theme.palette.divider
  }

  const getBackgroundColor = (): string => {
    if (error) return alpha(theme.palette.error.main, colors.opacity.hover)
    if (dragActive) return alpha(theme.palette.primary.main, colors.opacity.selected)
    return 'transparent'
  }

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={onClick}
      sx={{
        ...dropzoneBaseSx,
        border: `${borders.width.thin} ${getBorderStyle()} ${getBorderColor()}`,
        backgroundColor: getBackgroundColor(),
        '&:hover': error
          ? undefined
          : {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, colors.opacity.selected),
            },
        ...sx,
      }}
      {...boxProps}
    >
      <Box
        sx={{
          width: spacing.lg,
          height: spacing.lg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon
          name='description'
          size={typography.fontSize.xl}
          color={theme.palette.text.secondary}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.micro }}>
        <Link
          component='span'
          underline='hover'
          sx={{
            cursor: 'pointer',
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          {linkLabel}
        </Link>
        <Typography
          variant='body1'
          sx={{
            color: 'text.primary',
          }}
        >
          or drag and drop
        </Typography>
      </Box>
      <Typography
        variant='body2'
        color={error ? 'error.main' : 'textSecondary'}
        sx={{
          textAlign: 'center',
        }}
      >
        {error && errorMessage ? errorMessage : helperText}
      </Typography>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// FileUploadItem
// ---------------------------------------------------------------------------

export type FileUploadItemStatus = 'uploading' | 'complete' | 'error'

export interface FileUploadItemProps {
  /** Display name of the file */
  fileName: string
  /** Formatted file size string (e.g. "100kb") */
  fileSize?: string
  /** Current upload status */
  status: FileUploadItemStatus
  /** Upload progress 0–100 (used when status is 'uploading' or 'complete') */
  progress?: number
  /** Error message shown when status is 'error' (e.g. "File too large") */
  errorMessage?: string
  /** Callback when the delete/remove button is clicked */
  onDelete?: () => void
}

const itemContainerSx = {
  display: 'flex',
  alignItems: 'center',
  gap: spacing.xs,
  overflow: 'hidden',
  p: spacing.xs,
  border: `${borders.width.thin} solid`,
  borderColor: 'divider',
  borderRadius: borderRadius.sm,
} as const

export const FileUploadItem: React.FC<FileUploadItemProps> = ({
  fileName,
  fileSize,
  status,
  progress = 0,
  errorMessage,
  onDelete,
}) => {
  const theme = useTheme()

  const isError = status === 'error'
  const textColor = isError ? 'error.main' : 'text.primary'
  const secondaryColor = isError ? 'error.main' : 'text.secondary'

  const displayName = fileName

  const statusLabel =
    status === 'uploading' ? 'Loading' : status === 'complete' ? 'Complete' : 'Failed'
  const sizeStatusText = isError
    ? `${errorMessage || 'Error'} • ${statusLabel}`
    : `${fileSize || ''} • ${statusLabel}`

  const progressValue = status === 'complete' ? 100 : progress

  return (
    <Box sx={itemContainerSx}>
      <Box
        sx={{
          width: spacing.lg,
          height: spacing.lg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon
          name='description'
          size={typography.fontSize.xl}
          color={isError ? theme.palette.error.main : theme.palette.text.secondary}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant='body1' color={textColor} noWrap>
          {displayName}
        </Typography>
        <Typography variant='body2' color={secondaryColor}>
          {sizeStatusText}
        </Typography>
        {status === 'uploading' && (
          <Box sx={{ mt: spacing.pico }}>
            <LinearProgress
              variant='determinate'
              value={progressValue}
              color='primary'
              sx={{ width: maxWidth.menuSm }}
            />
          </Box>
        )}
      </Box>

      {onDelete && (
        <IconButton size='small' onClick={onDelete} aria-label='Remove file'>
          <Icon name='delete' fontSize='small' />
        </IconButton>
      )}
    </Box>
  )
}

// ---------------------------------------------------------------------------
// FileUpload (composed wrapper)
// ---------------------------------------------------------------------------

export interface FileUploadProps {
  children: React.ReactNode
}

const FileUpload: React.FC<FileUploadProps> = ({ children }) => {
  return <Stack spacing={spacing.xxs}>{children}</Stack>
}

export default FileUpload
